import React, { useState } from 'react';
import styled from 'styled-components';
import { images } from '../../../assets/images';
import { isValidEmail, validatePassword } from '../../../utils/password-policy';
import {
  AuthCard,
  AuthInner,
  AuthLogo,
  AuthTitle,
  AuthFooterRow,
  AuthLink,
  AuthInput,
  AuthSubmitButton,
  ErrorBanner,
  PasswordChecklist,
  TermsNotice,
} from './AuthUi';

export interface SignupScreenProps {
  onSignup: (email: string, password: string, name?: string) => void;
  onBackToLogin: () => void;
  loading: boolean;
  errorMessage?: string | null;
}

export const SignupScreen = (props: SignupScreenProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const pwValid = validatePassword(password).valid;
  const emailError = touched && !isValidEmail(email) ? 'Enter a valid email.' : null;
  const confirmError = touched && confirm !== password ? 'Passwords do not match.' : null;

  const submit = () => {
    setTouched(true);
    if (!isValidEmail(email) || !pwValid || confirm !== password) return;
    props.onSignup(email.trim(), password, name.trim() || undefined);
  };

  return (
    <AuthCard>
      <AuthInner>
        <AuthLogo src={images.risenHopeLogo} alt="Risen Hope" />
        <AuthTitle>Create your account</AuthTitle>

        <AuthInput label="Name (optional)" value={name} onChange={setName} placeholder="Your name" />
        <Spacer />
        <AuthInput
          label="Email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          type="email"
          error={emailError}
        />
        <Spacer />
        <AuthInput
          label="Password"
          value={password}
          onChange={v => setPassword(v)}
          placeholder="Create a password"
          type="password"
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
        />
        {(passwordFocused || password.length > 0) && <PasswordChecklist password={password} />}
        <Spacer />
        <AuthInput
          label="Confirm password"
          value={confirm}
          onChange={setConfirm}
          placeholder="Re-enter your password"
          type="password"
          error={confirmError}
          onKeyDown={e => {
            if (e.key === 'Enter') submit();
          }}
        />

        {!!props.errorMessage && (
          <>
            <Spacer />
            <ErrorBanner message={props.errorMessage} />
          </>
        )}

        <Spacer $size={20} />
        <TermsNotice />
        <Spacer $size={16} />
        <AuthSubmitButton title="Create account" onClick={submit} loading={props.loading} />

        <AuthFooterRow>
          <span>Already have an account?</span>
          <AuthLink onClick={props.onBackToLogin}>Log in</AuthLink>
        </AuthFooterRow>
      </AuthInner>
    </AuthCard>
  );
};

const Spacer = styled.div<{ $size?: number }>`
  height: ${p => p.$size ?? 16}px;
`;
